import React, { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function CompetitionRegistration() {
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState('');
    const [competitors, setCompetitors] = useState([]);
    const [competitions, setCompetitions] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Fetch events on component mount
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                const enabledEvents = data.filter(event => event.Enabled);
                setEvents(enabledEvents);
                if (enabledEvents.length > 0) {
                    setSelectedEvent(enabledEvents[0].EventID);
                }
            } catch (error) {
                console.error('Error fetching events:', error);
                showMessage('Failed to load events.', 'error');
            }
        };

        fetchEvents();
    }, []);

    // Fetch data when event is selected
    useEffect(() => {
        if (selectedEvent) {
            fetchEventData(selectedEvent);
        }
    }, [selectedEvent]);

    const fetchEventData = async (eventId) => {
        setLoading(true);
        try {
            // Fetch competitors for this event
            const competitorsResponse = await fetch(`${API_BASE_URL}/getAllCompetitors?EventID=${eventId}`);
            const competitorsData = await competitorsResponse.json();
            setCompetitors(Array.isArray(competitorsData) ? competitorsData : []);

            // Fetch competitions for this event
            const competitionsResponse = await fetch(`${API_BASE_URL}/getCompetitions?EventID=${eventId}`);
            const competitionsData = await competitionsResponse.json();
            // Filter to only show individual competitions that require registration
            const registrationRequiredCompetitions = Array.isArray(competitionsData) 
                ? competitionsData.filter(comp => !comp.TeamBased && comp.RegistrationRequired)
                : [];
            setCompetitions(registrationRequiredCompetitions);

            // Fetch existing registrations
            const registrationsResponse = await fetch(`${API_BASE_URL}/getRegistrations?EventID=${eventId}`);
            const registrationsData = await registrationsResponse.json();
            setRegistrations(Array.isArray(registrationsData) ? registrationsData : []);
        } catch (error) {
            console.error('Error fetching data:', error);
            showMessage('Failed to load data. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEventChange = (e) => {
        setSelectedEvent(Number(e.target.value));
    };

    // Check if a competitor is registered for a competition
    const isRegistered = (competitorId, competitionId) => {
        return registrations.some(reg => 
            reg.CompetitorID === competitorId && 
            reg.CompetitionID === competitionId
        );
    };

    // Handle checkbox change
    const handleRegistrationChange = async (competitorId, competitionId, isChecked) => {
        try {
            if (isChecked) {
                // Create new registration
                const response = await fetch(`${API_BASE_URL}/createRegistration`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        CompetitionID: competitionId,
                        CompetitorID: competitorId,
                        EventID: selectedEvent
                    })
                });

                if (response.ok) {
                    // Update local state (silently)
                    setRegistrations([
                        ...registrations, 
                        { 
                            CompetitionID: competitionId, 
                            CompetitorID: competitorId, 
                            EventID: selectedEvent 
                        }
                    ]);
                    // No success message displayed
                } else {
                    const errorData = await response.json();
                    showMessage(`Error: ${errorData.error || 'Failed to save registration.'}`, 'error');
                }
            } else {
                // Delete registration
                const response = await fetch(`${API_BASE_URL}/deleteRegistration`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        CompetitionID: competitionId,
                        CompetitorID: competitorId,
                        EventID: selectedEvent
                    })
                });

                if (response.ok) {
                    // Update local state (silently)
                    setRegistrations(
                        registrations.filter(reg => 
                            !(reg.CompetitorID === competitorId && 
                              reg.CompetitionID === competitionId)
                        )
                    );
                    // No success message displayed
                } else {
                    const errorData = await response.json();
                    showMessage(`Error: ${errorData.error || 'Failed to remove registration.'}`, 'error');
                }
            }
        } catch (error) {
            console.error('Error updating registration:', error);
            showMessage('Failed to update registration. Please try again.', 'error');
        }
    };

    const showMessage = (text, type) => {
        setMessage({ text, type });
        // Clear message after 3 seconds
        setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    };

    return (
        <div className="p-1">
            {message.text && (
                <div className={`alert ${message.type === 'error' ? 'alert-danger' : 'alert-success'}`}>
                    {message.text}
                </div>
            )}
            
            <div className="flex mb-1">
                <label className="form-control-inline">
                    <select 
                        className="form-select" 
                        value={selectedEvent || ''} 
                        onChange={handleEventChange}
                    >
                        <option value="">Select Event</option>
                        {events.map(event => (
                            <option key={event.EventID} value={event.EventID}>
                                {event.EventName}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            {loading ? (
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading data...</p>
                </div>
            ) : selectedEvent ? (
                competitors.length > 0 && competitions.length > 0 ? (
                    <div className="data-table-wrapper">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Competitor Name</th>
                                    <th>Organization</th>
                                    {competitions.map(competition => (
                                        <th key={competition.CompetitionID}>
                                            {competition.CompetitionName}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {competitors.map(competitor => (
                                    <tr key={competitor.CompetitorID}>
                                        <td>{competitor.CompetitorID}</td>
                                        <td>{`${competitor.FirstName} ${competitor.LastName}`}</td>
                                        <td>{competitor.Organization}</td>
                                        {competitions.map(competition => (
                                            <td 
                                                key={`${competitor.CompetitorID}-${competition.CompetitionID}`} 
                                                className="text-center"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={isRegistered(competitor.CompetitorID, competition.CompetitionID)}
                                                    onChange={(e) => handleRegistrationChange(
                                                        competitor.CompetitorID, 
                                                        competition.CompetitionID, 
                                                        e.target.checked
                                                    )}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="alert alert-info">
                        {competitions.length === 0 ? 
                            "No competitions that require registration were found for this event." : 
                            "No competitors found for this event."}
                    </div>
                )
            ) : (
                <div className="alert alert-info">
                    Please select an event to manage competition registrations.
                </div>
            )}
        </div>
    );
}
